/** The Run Kinematics report tab */

import {
  KneePositionGraph,
  PelvisFunctionGraph,
  ProximodistalSequenceGraph,
  ScissorsColumnChart,
  ThighBarGraph,
  ThighCoordinationGraph,
} from '@/pages/ReportV2/components/Graphs';
import {
  InterLimbCoordinationModalContent,
  KneePositionsModalContent,
  PelvisAngularExcursionModalContent,
  ProximodistalSequenceModalContent,
} from '@/pages/ReportV2/components/Modals';

import '@/pages/ReportV2/components/Graphs/graphCard.css';

import { IonCardContent, IonCol, IonFabButton, IonGrid, IonIcon, IonRow, IonText } from '@ionic/react';
import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';

import { CardHeader } from '@/components/CardHeader/cardHeader';
import { Card } from '@/components/Cards';
import { DataTable } from '@/components/DataTable/DataTable';
import { DataTableColumn } from '@/components/DataTable/DataTableColumn';
import { DataTableHeader } from '@/components/DataTable/DataTableHeader';
import { DataTableSection } from '@/components/DataTable/DataTableSection';
import { Icon } from '@/components/Icon';
import { ReportModal } from '@/components/Modal/ReportModal';
import { InfoToast } from '@/components/Toasts/InfoToast';

import ToggleLeft from '@/assets/icons/toggle-left.svg';

import { RunHamstringFunctionData } from '@/models/reportV2.model';

type RunHamstringFunctionReportTabProps = {
  tabData: RunHamstringFunctionData;
  setFloatingButtons: (buttons: React.ReactNode) => void;
};

/** Run kinematics report tab
 * @param report - the full run report info and data
 */
export const RunHamstringFunctionReportTab = ({ tabData, setFloatingButtons }: RunHamstringFunctionReportTabProps) => {
  const [showInfoToast, setShowInfoToast] = useState(false);
  const [showAllStrides, setShowAllStrides] = useState<boolean>(true);

  const toggleShowAllStrides = () => {
    setShowAllStrides((prev) => {
      const newValue = !prev;
      localStorage.setItem('showAllStridesHamstringFunction', newValue ? 'true' : 'false');
      return newValue;
    });
  };

  const seriesPelvisLeft = useMemo(() => {
    return showAllStrides ? tabData.pelvisKinematicData.allStrides.Left : tabData.pelvisKinematicData.mean.Left;
  }, [showAllStrides, tabData]);

  const seriesPelvisRight = useMemo(() => {
    return showAllStrides ? tabData.pelvisKinematicData.allStrides.Right : tabData.pelvisKinematicData.mean.Right;
  }, [showAllStrides, tabData]);

  const seriesKneePositionsLeft = useMemo(() => {
    return showAllStrides ? tabData.kneePositionsData.kneePositionsLeft : tabData.kneePositionsData.kneePositionLeft;
  }, [showAllStrides, tabData]);

  const seriesKneePositionsRight = useMemo(() => {
    return showAllStrides ? tabData.kneePositionsData.kneePositionsRight : tabData.kneePositionsData.kneePositionRight;
  }, [showAllStrides, tabData]);

  const [showZoomModal, setShowZoomModal] = useState({
    show: false,
    graph: '',
  });

  useEffect(() => {
    setShowInfoToast(true);
    return () => {
      setShowInfoToast(false);
    };
  }, []);

  useEffect(() => {
    const buttons = (
      <>
        <IonFabButton onClick={() => toggleShowAllStrides()} title={'Toggle Mean / All Strides'}>
          <IonIcon icon={ToggleLeft}></IonIcon>
        </IonFabButton>
        <IonFabButton onClick={() => setShowInfoToast(true)} title={'Show Info'}>
          <Icon name={'info'}></Icon>
        </IonFabButton>
      </>
    );
    setFloatingButtons(buttons); // Update buttons in parent
    return () => setFloatingButtons(null); // Clean up on unmount
  }, [setFloatingButtons]);

  if (!tabData) {
    return <IonText>Data not found.</IonText>;
  }
  return (
    <>
      <ReportModal
        isOpen={showZoomModal.show}
        onDidDismiss={() => setShowZoomModal({ show: false, graph: '' })}
        setShowInfo={() => setShowZoomModal({ show: false, graph: '' })}>
        <IonGrid className='' style={{ height: '100%' }}>
          {showZoomModal.graph === 'Pelvis' && (
            <PelvisAngularExcursionModalContent
              pelvisAngularExcursionGraphAnnotations={tabData.pelvisAngularExcursionGraphAnnotations}
              pelvisKinematicData={tabData.pelvisKinematicData}
              setShowZoomModal={setShowZoomModal}
            />
          )}
          {showZoomModal.graph === 'Knee Positions' && (
            <KneePositionsModalContent data={tabData.kneePositionsData} setShowZoomModal={setShowZoomModal} />
          )}
          {showZoomModal.graph === 'Proximodistal Sequence' && (
            <ProximodistalSequenceModalContent
              EuclideanData={tabData.euclideanNormData}
              cycleEventAnnotations_Left={tabData.thighCoordinationData.Left.annotations}
              cycleEventAnnotations_Right={tabData.thighCoordinationData.Right.annotations}
              proximodistalSequenceData={tabData.proximoDistalSequenceAnnotations}
              setShowZoomModal={setShowZoomModal}
              xAxisLeft={tabData.annotations_Left[3]}
              xAxisRight={tabData.annotations_Right[3]}
            />
          )}
          {showZoomModal.graph === 'InterLimb Coordination' && (
            <InterLimbCoordinationModalContent
              annotations={true}
              coordinativeVariabilityData_Left={tabData.thighCoordinationData.Left.coordinativeVariability}
              coordinativeVariabilityData_Right={tabData.thighCoordinationData.Right.coordinativeVariability}
              crp_Left={tabData.thighCoordinationData.Left.crpData}
              crp_Right={tabData.thighCoordinationData.Right.crpData}
              graphAnnotations_Left={tabData.thighCoordinationData.Left.annotations}
              graphAnnotations_Right={tabData.thighCoordinationData.Right.annotations}
              setShowMoreInfoModal={setShowZoomModal}
            />
          )}
        </IonGrid>
      </ReportModal>

      {/* Pelvis Angular Excursion */}
      <IonRow className='ion-no-padding'>
        <IonCol className='' size='12'>
          <Card className='' onClick={() => setShowZoomModal({ show: true, graph: 'Pelvis' })} style={{ margin: 0 }}>
            <CardHeader title={'Pelvis Angular Excursion'}></CardHeader>

            <IonCardContent>
              <IonRow>
                <IonCol className='ion-no-padding' size='6'>
                  <PelvisFunctionGraph
                    data={seriesPelvisLeft.series}
                    graphAnnotations={tabData.pelvisAngularExcursionGraphAnnotations.Left}
                    strokeWidth={showAllStrides ? 1 : 2}
                    subtitle={'Anterior tilt / Drop (-) | Posterior tilt / Hike (+)'}
                    title={'Left'}
                    yAxis={seriesPelvisLeft.yAxis}
                  />
                </IonCol>
                <IonCol className='ion-no-padding' size='6'>
                  <PelvisFunctionGraph
                    data={seriesPelvisRight.series}
                    graphAnnotations={tabData.pelvisAngularExcursionGraphAnnotations.Right}
                    strokeWidth={showAllStrides ? 1 : 2}
                    subtitle={'Anterior tilt / Drop (-) | Posterior tilt / Hike (+)'}
                    title={'Right'}
                    yAxis={seriesPelvisRight.yAxis}
                  />
                </IonCol>
              </IonRow>
            </IonCardContent>
          </Card>
        </IonCol>
      </IonRow>
      {/* Thigh / Thigh Coordination */}
      <IonRow className='ion-no-padding'>
        <IonCol className='' size='12'>
          <Card
            className=''
            onClick={() => setShowZoomModal({ show: true, graph: 'InterLimb Coordination' })}
            style={{ margin: 0 }}>
            <CardHeader title={'Inter-Thigh Coordination'}></CardHeader>

            <IonCardContent>
              <IonRow>
                <IonCol className='ion-no-padding' size='6'>
                  <ThighCoordinationGraph
                    coordinativeVariabilityData={tabData.thighCoordinationData.Left.coordinativeVariability}
                    crpData={tabData.thighCoordinationData.Left.crpData}
                    graphAnnotations={tabData.thighCoordinationData.Left.annotations}
                    strokeWidth={2}
                    title={'Left'}
                  />
                </IonCol>
                <IonCol className='ion-no-padding' size='6'>
                  <ThighCoordinationGraph
                    coordinativeVariabilityData={tabData.thighCoordinationData.Right.coordinativeVariability}
                    crpData={tabData.thighCoordinationData.Right.crpData}
                    graphAnnotations={tabData.thighCoordinationData.Right.annotations}
                    strokeWidth={2}
                    title={'Right'}
                  />
                </IonCol>
              </IonRow>
            </IonCardContent>
          </Card>
        </IonCol>
      </IonRow>
      {/* Knee Positions */}
      <IonRow className='ion-no-padding'>
        <IonCol className='' size='12'>
          <Card
            className=''
            onClick={() => setShowZoomModal({ show: true, graph: 'Knee Positions' })}
            style={{ margin: 0 }}>
            <CardHeader title={'Knee Positions'}></CardHeader>

            <IonCardContent>
              <IonRow>
                <IonCol className='ion-no-padding' size='6'>
                  <KneePositionGraph
                    data={seriesKneePositionsLeft}
                    strokeWidth={showAllStrides ? 1 : 2}
                    subtitle={`Max Knee Height: ${tabData.kneePositionsData.maxKneePositions.col0[0].toFixed(2) + ' cm'}`}
                    title={'Left'}
                  />
                </IonCol>
                <IonCol className='ion-no-padding' size='6'>
                  <KneePositionGraph
                    data={seriesKneePositionsRight}
                    strokeWidth={showAllStrides ? 1 : 2}
                    subtitle={`Max Knee Height: ${tabData.kneePositionsData.maxKneePositions.col1[0].toFixed(2) + ' cm'}`}
                    title={'Right'}
                  />
                </IonCol>
              </IonRow>
            </IonCardContent>
          </Card>
        </IonCol>
      </IonRow>
      {/* Proximodistal Sequence */}
      <IonRow className='ion-no-padding'>
        <IonCol className='' size='12'>
          <Card
            className=''
            onClick={() => setShowZoomModal({ show: true, graph: 'Proximodistal Sequence' })}
            style={{ margin: 0 }}>
            <CardHeader title={'Proximodistal Sequence'}></CardHeader>

            <IonCardContent>
              <IonRow>
                <IonCol className='ion-no-padding' size='6'>
                  <ProximodistalSequenceGraph
                    cycleEventAnnotations={tabData.thighCoordinationData.Left.annotations}
                    data={tabData.euclideanNormData.mean.Left.series}
                    graphAnnotations={tabData.proximoDistalSequenceAnnotations.Left}
                    title={'Left'}
                    xAxisStart={tabData.annotations_Left[3]}
                    yAxis={tabData.euclideanNormData.mean.Left.yAxis}
                  />
                </IonCol>
                <IonCol className='ion-no-padding' size='6'>
                  <ProximodistalSequenceGraph
                    cycleEventAnnotations={tabData.thighCoordinationData.Right.annotations}
                    data={tabData.euclideanNormData.mean.Right.series}
                    graphAnnotations={tabData.proximoDistalSequenceAnnotations.Right}
                    title={'Right'}
                    xAxisStart={tabData.annotations_Right[3]}
                    yAxis={tabData.euclideanNormData.mean.Right.yAxis}
                  />
                </IonCol>
              </IonRow>
            </IonCardContent>
          </Card>
        </IonCol>
      </IonRow>
      <IonRow className='ion-no-padding'>
        <IonCol className='' size='4'>
          <ScissorsColumnChart
            data={tabData.scissorsBarGraphData}
            title={'Scissors velocity'}
            yAxisLabel={tabData.HamstringData.yAxisLabelVelocity}
          />
        </IonCol>
        <IonCol className='' size='4'>
          <ThighBarGraph
            data={tabData.HamstringData.thighVelocityBarData}
            stdData={tabData.HamstringData.thighVelocityBarData_std}
            title={'Hamstring propulsion - Velocity'}
            xAxisCategories={['Peak TSw', 'AV. TSw', 'Peak TSt', 'Av. Stance']}
            yAxisLabel={tabData.HamstringData.yAxisLabelVelocity}
          />
        </IonCol>
        <IonCol className='' size='4'>
          <ThighBarGraph
            data={tabData.HamstringData.thighAccelerationBarData}
            stdData={tabData.HamstringData.thighAccelerationBarData_std}
            title={'Hamstring propulsion - Acceleration'}
            xAxisCategories={['TSw', 'Stance']}
            yAxisLabel={tabData.HamstringData.yAxisLabelAcceleration}
          />
        </IonCol>
      </IonRow>

      {/* Scissors Table */}
      <IonRow className='ion-no-padding'>
        <IonCol className='' size='12'>
          <Card style={{ margin: 0 }}>
            <CardHeader title={'Scissors Velocity & Acceleration'} />
            <IonCardContent>
              <DataTable>
                <DataTableHeader titles={['', 'Left', 'Right', 'Symmetry']} />
                <DataTableSection title={'Scissors'}>
                  <IonRow style={{ width: '100%' }}>
                    <DataTableColumn type='key'>Average Velocity</DataTableColumn>
                    <DataTableColumn type='value'>
                      {tabData.scissorsTableData.scissors.averageVelocity.left +
                        ' ' +
                        tabData.scissorsTableData.scissors.averageVelocity.unit}
                    </DataTableColumn>
                    <DataTableColumn type='value'>
                      {tabData.scissorsTableData.scissors.averageVelocity.right +
                        ' ' +
                        tabData.scissorsTableData.scissors.averageVelocity.unit}
                    </DataTableColumn>
                    <DataTableColumn type='value'>
                      {tabData.scissorsTableData.scissors.averageVelocity.symmetry + ' %'}
                    </DataTableColumn>
                  </IonRow>
                </DataTableSection>

                <DataTableSection title={'Average Velocities'}>
                  <IonRow>
                    <DataTableColumn type='key'>Terminal Swing</DataTableColumn>
                    <DataTableColumn type='value'>
                      {tabData.scissorsTableData.averageVelocities.terminalSwing.left +
                        ' ' +
                        tabData.scissorsTableData.averageVelocities.terminalSwing.unit}
                    </DataTableColumn>
                    <DataTableColumn type='value'>
                      {tabData.scissorsTableData.averageVelocities.terminalSwing.right +
                        ' ' +
                        tabData.scissorsTableData.averageVelocities.terminalSwing.unit}
                    </DataTableColumn>
                    <DataTableColumn type='value'>
                      {tabData.scissorsTableData.averageVelocities.terminalSwing.symmetry + ' %'}
                    </DataTableColumn>
                  </IonRow>
                  <IonRow>
                    <DataTableColumn type='key'>Stance</DataTableColumn>
                    <DataTableColumn type='value'>
                      {tabData.scissorsTableData.averageVelocities.stance.left +
                        ' ' +
                        tabData.scissorsTableData.averageVelocities.stance.unit}
                    </DataTableColumn>
                    <DataTableColumn type='value'>
                      {tabData.scissorsTableData.averageVelocities.stance.right +
                        ' ' +
                        tabData.scissorsTableData.averageVelocities.stance.unit}
                    </DataTableColumn>
                    <DataTableColumn type='value'>
                      {tabData.scissorsTableData.averageVelocities.stance.symmetry + ' %'}
                    </DataTableColumn>
                  </IonRow>
                </DataTableSection>
                <DataTableSection title={'Peak velocities and accelerations'}>
                  <IonRow>
                    <DataTableColumn type='key'>Velocity Terminal Swing</DataTableColumn>
                    <DataTableColumn type='value'>
                      {tabData.scissorsTableData.peakVelocitiesAndAccelerations.velocity.terminalSwing.left +
                        ' ' +
                        tabData.scissorsTableData.peakVelocitiesAndAccelerations.velocity.terminalSwing.unit}
                    </DataTableColumn>
                    <DataTableColumn type='value'>
                      {tabData.scissorsTableData.peakVelocitiesAndAccelerations.velocity.terminalSwing.right +
                        ' ' +
                        tabData.scissorsTableData.peakVelocitiesAndAccelerations.velocity.terminalSwing.unit}
                    </DataTableColumn>
                    <DataTableColumn type='value'>
                      {tabData.scissorsTableData.peakVelocitiesAndAccelerations.velocity.terminalSwing.symmetry + ' %'}
                    </DataTableColumn>
                  </IonRow>
                  <IonRow>
                    <DataTableColumn type='key'>Velocity Terminal Stance</DataTableColumn>
                    <DataTableColumn type='value'>
                      {tabData.scissorsTableData.peakVelocitiesAndAccelerations.velocity.stance.left +
                        ' ' +
                        tabData.scissorsTableData.peakVelocitiesAndAccelerations.velocity.stance.unit}
                    </DataTableColumn>
                    <DataTableColumn type='value'>
                      {tabData.scissorsTableData.peakVelocitiesAndAccelerations.velocity.stance.right +
                        ' ' +
                        tabData.scissorsTableData.peakVelocitiesAndAccelerations.velocity.stance.unit}
                    </DataTableColumn>
                    <DataTableColumn type='value'>
                      {tabData.scissorsTableData.peakVelocitiesAndAccelerations.velocity.stance.symmetry + ' %'}
                    </DataTableColumn>
                  </IonRow>
                  <IonRow>
                    <DataTableColumn type='key'>Acceleration Terminal Swing</DataTableColumn>
                    <DataTableColumn type='value'>
                      {tabData.scissorsTableData.peakVelocitiesAndAccelerations.acceleration.terminalSwing.left +
                        ' ' +
                        tabData.scissorsTableData.peakVelocitiesAndAccelerations.acceleration.terminalSwing.unit}
                    </DataTableColumn>
                    <DataTableColumn type='value'>
                      {tabData.scissorsTableData.peakVelocitiesAndAccelerations.acceleration.terminalSwing.right +
                        ' ' +
                        tabData.scissorsTableData.peakVelocitiesAndAccelerations.acceleration.terminalSwing.unit}
                    </DataTableColumn>
                    <DataTableColumn type='value'>
                      {tabData.scissorsTableData.peakVelocitiesAndAccelerations.acceleration.terminalSwing.symmetry +
                        ' %'}
                    </DataTableColumn>
                  </IonRow>
                  <IonRow>
                    <DataTableColumn type='key'>Acceleration Stance</DataTableColumn>
                    <DataTableColumn type='value'>
                      {tabData.scissorsTableData.peakVelocitiesAndAccelerations.acceleration.stance.left +
                        ' ' +
                        tabData.scissorsTableData.peakVelocitiesAndAccelerations.acceleration.stance.unit}
                    </DataTableColumn>
                    <DataTableColumn type='value'>
                      {tabData.scissorsTableData.peakVelocitiesAndAccelerations.acceleration.stance.right +
                        ' ' +
                        tabData.scissorsTableData.peakVelocitiesAndAccelerations.acceleration.stance.unit}
                    </DataTableColumn>
                    <DataTableColumn type='value'>
                      {tabData.scissorsTableData.peakVelocitiesAndAccelerations.acceleration.stance.symmetry + ' %'}
                    </DataTableColumn>
                  </IonRow>
                </DataTableSection>
              </DataTable>
            </IonCardContent>
          </Card>
        </IonCol>
      </IonRow>

      <InfoToast
        isOpen={showInfoToast}
        message='All graphs can be enlarged by clicking / tapping on the card.'
        onClose={() => setShowInfoToast(false)}
      />
    </>
  );
};
